import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { gql, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import c from 'classnames';
import { $getNodeByKey, $getRoot } from 'lexical';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import { match, P } from 'ts-pattern';
import * as zod from 'zod';
import { z } from 'zod';
import {
  ContactInteractionMessageTemplate,
  ContactInteractionMessageTemplateWhereInput,
  Mutation,
  MutationDeleteContactInteractionMessageTemplateArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { ModalsZIndex } from 'src/components/constants';
import {
  LggDropdownButtonWithoutOverlay,
  LggOptionsDropdownButtonWithCustomTrigger,
  LggSelectableOptionsDropdownButton,
} from 'src/components/general/button/dropdown-button';
import { IconButtonV2 } from 'src/components/general/button/icon-button';
import { ButtonV2 } from 'src/components/general/button/lgg-button';
import {
  DrawerOption,
  OptionsBottomDrawer,
} from 'src/components/general/drawer/bottom/options-bottom-drawer';
import { SelectableOptionsDrawer } from 'src/components/general/drawer/bottom/selectable-options-bottom-drawer';
import { useShowConfirmationModal } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { ListLoadingItem } from 'src/components/general/feedback/list-loading-item';
import { Icon } from 'src/components/general/icon';
import { preventDefaultAndStopPropagation } from 'src/components/general/inputs/helpers';
import { TemplatePickerModal } from 'src/components/general/modals/template-picker-modal';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { LoadingIndicator } from 'src/components/loading-fallback';
import { ContactInteractionInputManagerContext } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interaction-input-manager';
import { MessageTemplatesFilters } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/contact-interaction-message-templates/message-templates-filter';
import { useContactInteractionMessageTemplatesQuery } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/hooks/use-contact-interaction-message-templates-query';
import { UpsertMessageTemplateModal } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-input-area/upsert-message-template-modal';
import { TemplatePresetPlaceholderResolver } from 'src/components/pages/conversations/components/general/lexical-editor/nodes/template-placeholders-nodes/placeholders-resolvers/template-preset-placeholder-resolver';
import {
  BasePluginProps,
  PluginIcon,
} from 'src/components/pages/conversations/components/general/lexical-editor/plugins/shared';
import { getPresetPlaceholderValue } from 'src/components/pages/conversations/components/general/text-placeholders-helpers';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useCurrentUserId } from 'src/hooks/use-current-user';
import { useVisible } from 'src/hooks/use-visible';

export enum MessageTemplateVisibility {
  ALL = 'ALL',
  CREATED_BY_ME = 'CREATED_BY_ME',
  EVERYONE = 'EVERYONE',
  ONLY_ME = 'ONLY_ME',
}

const LoadingContainer = styled(FlexColumn)`
  align-items: center;
  height: 100%;
  justify-content: center;
  width: 100%;
`;

const EmptyStateContainer = styled(FlexColumn)`
  align-items: center;
  height: 100%;
  justify-content: flex-start;
  margin: 15px 20px;

  ${up('md')} {
    justify-content: center;
    margin: 0;
  }
`;

const EmptyStateIcon = styled(Icon)`
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.porcelain2};
  padding: 30px;
  margin: 60px 0 20px;
  width: 90px;

  ${up('md')} {
    margin-top: 0;
  }

  svg {
    height: 30px;
    width: 30px;

    path {
      fill: ${({ theme }) => theme.colors.gogo};
    }
  }
`;

const EmptyStateTitle = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  text-align: center;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 18px;
  line-height: 23px;
  letter-spacing: -0.36px;
  margin-bottom: 5px;
  max-width: 400px;
`;

const EmptyStateDescription = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  text-align: center;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
  margin-bottom: 20px;
  max-width: 400px;
`;

type ListEmptyStateProps = {
  onAddMessageTemplate: VoidFunction;
};

const ListEmptyState = ({ onAddMessageTemplate }: ListEmptyStateProps) => {
  const { t } = useTranslation(['conversations']);

  return (
    <EmptyStateContainer data-lgg-id="message-templates-list-empty-state">
      <EmptyStateIcon type="messageTemplates" />
      <EmptyStateTitle>
        {t(
          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.emptyState.title',
        )}
      </EmptyStateTitle>
      <EmptyStateDescription>
        {t(
          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.emptyState.description',
        )}
      </EmptyStateDescription>
      <ButtonV2 size="small" variant="primary" onClick={onAddMessageTemplate}>
        {t(
          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.emptyState.addTemplateButtonLabel',
        )}
      </ButtonV2>
    </EmptyStateContainer>
  );
};

const TemplateListItemContainer = styled.div`
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  padding: 10px;

  &.selected {
    background-color: ${({ theme }) => theme.colors.secondaryTopaz10};
    border: 1px solid ${({ theme }) => theme.colors.secondaryTopaz60};
  }
`;

const TemplateListItemTopContainer = styled(FlexRow)`
  justify-content: space-between;
  position: relative;
`;

const TemplateListItemTitle = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 14px;
  line-height: 18px;
  margin-bottom: 10px;
`;

const TemplateListItemMoreOptionsIcon = styled(Icon)`
  cursor: pointer;
  height: 100%;
  left: 5px;
  position: relative;
  top: 2px;

  svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }

  &:hover {
    svg {
      path {
        fill: ${({ theme }) => theme.colors.smalt};
      }
    }
  }
`;

const TemplateListItemPreview = styled.div`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  color: ${({ theme }) => theme.colors.storm};
  display: -webkit-box;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 14px;
  letter-spacing: -0.196px;
  line-height: 18px;
  margin-bottom: 10px;
  max-height: 72px;
  overflow: hidden;
`;

const TemplateListItemOuterContainer = styled.div`
  &:not(.first) {
    border-top: 1px solid ${({ theme }) => theme.colors.porcelain};
  }

  padding: 10px;
`;

const TemplateListItemDetailsContainer = styled(FlexRow)`
  color: ${({ theme }) => theme.colors.flint};
  line-height: 15px;

  .separator {
    margin: 0 3px;
  }
`;

const TemplateListItemDetailContainer = styled.span`
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 12px;
  line-height: 15px;

  .title {
    color: ${({ theme }) => theme.colors.storm};
    text-transform: capitalize;
  }
`;

const DeleteIcon = styled(Icon)`
  svg {
    height: 18px;
    width: 18px;

    ${up('md')} {
      height: 16px;
      width: 16px;
    }

    path {
      fill: ${({ theme }) => theme.colors.secondaryCoral};
    }
  }
`;

const VisibilityFilterDropdown = styled(LggSelectableOptionsDropdownButton)`
  min-width: 140px;
  margin-right: 15px;
  padding: 8px 10px;
  width: 100%;

  & > .lgg-icon {
    margin-right: 5px;
  }

  &.arrow-down-icon {
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const MobileDropdownButton = styled(LggDropdownButtonWithoutOverlay)`
  padding: 10px;

  &,
  &:hover {
    background-color: ${({ theme }) => theme.colors.white};
  }
`;

const MobileAddTemplateButton = styled(IconButtonV2)`
  margin-left: 15px;

  svg {
    width: 16px;
    height: 16px;
  }

  &,
  &:hover {
    background-color: ${({ theme }) => theme.colors.white};
  }
`;

const TemplateListFooter = styled.div`
  height: 30px;
`;

const TemplateListItemDetail = ({ title, value }: { title: string; value: string }) => {
  return (
    <TemplateListItemDetailContainer>
      <span className="title">{`${title}: `}</span>
      <span className="value">{value}</span>
    </TemplateListItemDetailContainer>
  );
};

const DELETE_TEMPLATE_MUTATION = gql`
  mutation DeleteContactInteractionMessageTemplate(
    $input: DeleteContactInteractionMessageTemplateInput!
  ) {
    deleteContactInteractionMessageTemplate(input: $input) {
      id
    }
  }
`;

const TemplateListItem = ({
  template,
  onClick,
  selected = false,
  isFirstItem = false,
  onEdit,
  onDelete,
}: {
  template: ContactInteractionMessageTemplate;
  onClick: VoidFunction;
  selected?: boolean;
  isFirstItem?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const { t } = useTranslation(['common', 'conversations']);
  const { name, messagePreview, department, language } = template;
  const moreOptionsVisibilityHandler = useVisible();
  const breakpointUpMd = useBreakpoint(up('md'));
  const showNotification = useShowNotification();
  const { id: institutionId } = useCurrentInstitution();
  const showConfirmationModal = useShowConfirmationModal();
  const currentUserId = useCurrentUserId();
  const showTemplateOptions = useMemo(
    () => template.createdBy.id === currentUserId,
    [currentUserId, template.createdBy.id],
  );

  const [deleteTemplate] = useMutation<
    Pick<Mutation, 'deleteContactInteractionMessageTemplate'>,
    MutationDeleteContactInteractionMessageTemplateArgs
  >(DELETE_TEMPLATE_MUTATION, {
    variables: {
      input: {
        companyId: institutionId,
        id: template.id,
      },
    },
    onCompleted: () => {
      showNotification({
        title: t(
          'conversations:messageInput.options.messageTemplates.upsertMessageTemplateModal.deletedSuccessMessage',
        ),
        type: 'success',
      });
      onDelete();
    },
  });

  const languageLabelMap = useMemo(
    () => ({
      ENGLISH: t('common:languages.english'),
      SPANISH: t('common:languages.spanish'),
    }),
    [t],
  );

  const templateListItemOptions: DrawerOption[] = useMemo(
    () => [
      {
        label: t('common:edit'),
        onClick: (e) => {
          preventDefaultAndStopPropagation(e);
          onEdit();
        },
        icon: 'edit',
        'data-lgg-id': 'template-list-item-edit-option',
      },
      {
        label: t('common:delete'),
        onClick: async (e) => {
          preventDefaultAndStopPropagation(e);

          showConfirmationModal({
            confirmButtonText: t('common:delete'),
            message: t(
              'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.deleteConfirmationModal.message',
            ),
            onConfirm: deleteTemplate,
            title: t(
              'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.deleteConfirmationModal.title',
            ),
            testId: 'delete-template-confirm-modal',
            confirmButtonType: 'delete',
          });
        },
        icon: <DeleteIcon type="deleteBin" />,
        'data-lgg-id': 'template-list-item-delete-option',
      },
    ],
    [deleteTemplate, onEdit, showConfirmationModal, t],
  );

  return (
    <TemplateListItemOuterContainer
      className={c({ first: isFirstItem })}
      data-lgg-id="contact-interaction-message-template"
      data-id={template.id}
    >
      <TemplateListItemContainer className={c({ selected })} onClick={onClick}>
        <TemplateListItemTopContainer>
          <TemplateListItemTitle>{name}</TemplateListItemTitle>
          <span>
            {showTemplateOptions ? (
              <TemplateListItemMoreOptionsIcon
                lggTestId="template-more-options"
                type="moreOptionsWithPadding"
                onClick={(e) => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  moreOptionsVisibilityHandler.show();
                }}
              />
            ) : null}
            {breakpointUpMd ? (
              <LggOptionsDropdownButtonWithCustomTrigger
                visibilityHandler={moreOptionsVisibilityHandler}
                options={templateListItemOptions}
                customDropdownProps={{
                  placement: 'bottomRight',
                  align: {
                    offset: [0, 28],
                  },
                  overlayStyle: {
                    width: '105px',
                  },
                }}
                customTrigger={<span></span>}
              />
            ) : (
              <OptionsBottomDrawer
                visible={moreOptionsVisibilityHandler.visible}
                zIndex={ModalsZIndex}
                title={t(
                  'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.templateOptions',
                )}
                onClose={moreOptionsVisibilityHandler.close}
                options={templateListItemOptions}
              />
            )}
          </span>
        </TemplateListItemTopContainer>
        <TemplateListItemPreview>{messagePreview}</TemplateListItemPreview>
        <TemplateListItemDetailsContainer>
          <TemplateListItemDetail
            title={t('common:language')}
            value={languageLabelMap[language]}
          />
          {department?.name ? (
            <>
              <span className="separator">{' – '}</span>
              <TemplateListItemDetail
                title={t('common:category')}
                value={department.name}
              />
            </>
          ) : null}
        </TemplateListItemDetailsContainer>
      </TemplateListItemContainer>
    </TemplateListItemOuterContainer>
  );
};

export enum OwnerFilterOption {
  ANYONE = 'ANYONE',
  IM_THE_OWNER = 'IM_THE_OWNER',
  IM_NOT_THE_OWNER = 'IM_NOT_THE_OWNER',
  SPECIFIC_PERSON = 'SPECIFIC_PERSON',
}

export const filtersFormSchema = zod.object({
  name: zod.string().nullish(),
  department: zod.array(z.number()).nullish(),
  language: zod.array(z.union([z.literal('ENGLISH'), z.literal('SPANISH')])).nullish(),
  shareWith: zod.array(z.union([z.literal('EVERYONE'), z.literal('ONLY_ME')])).nullish(),
  owner: zod.nativeEnum(OwnerFilterOption).nullish(),
  ownerList: z.array(z.number()).optional(),
});

export type FiltersFormValues = zod.infer<typeof filtersFormSchema>;

export const defaultFilterFormValues: FiltersFormValues = {
  department: [],
  language: [],
  shareWith: [],
  name: undefined,
  owner: OwnerFilterOption.ANYONE,
  ownerList: [],
};

export const MessageTemplatesPlugin = ({ disabled = false }: BasePluginProps) => {
  const breakpointUpMd = useBreakpoint(up('md'));
  const visibilityHandler = useVisible();
  const visibilityFilterVisibilityHandler = useVisible();
  const upsertMessageTemplateModalVisibilityHandler = useVisible();
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const [visibilityFilter, setVisibilityFilter] = useState<MessageTemplateVisibility>(
    MessageTemplateVisibility.ALL,
  );
  const [filterWhere, setWhere] = useState<ContactInteractionMessageTemplateWhereInput>(
    {},
  );
  const [selectedTemplate, setSelectedTemplate] = useState<
    ContactInteractionMessageTemplate | undefined
  >(undefined);
  const [templateToEdit, setTemplateToEdit] = useState<
    ContactInteractionMessageTemplate | undefined
  >(undefined);
  const { t } = useTranslation(['conversations', 'common']);
  const [editor] = useLexicalComposerContext();
  const { contact } = useContext(ContactInteractionInputManagerContext);
  const showConfirmationModal = useShowConfirmationModal();
  const currentUserId = useCurrentUserId();

  const filtersForm = useForm<FiltersFormValues>({
    resolver: zodResolver(filtersFormSchema),
    defaultValues: defaultFilterFormValues,
  });

  const getFilterWhereValue = useCallback(
    (values: Partial<FiltersFormValues>): ContactInteractionMessageTemplateWhereInput => {
      let result: ContactInteractionMessageTemplateWhereInput = {};

      const { name, language, department, shareWith, owner, ownerList } = values;

      if (name) {
        result = {
          ...result,
          name: {
            _contains: name,
          },
        };
      }

      if (language && language?.length > 0) {
        result = {
          ...result,
          language: {
            _in: [...language],
          },
        };
      }

      if (department && department?.length > 0) {
        result = {
          ...result,
          department: {
            id: {
              _in: [...department],
            },
          },
        };
      }

      if (shareWith && shareWith?.length > 0) {
        result = {
          ...result,
          shareWith: {
            _in: [...shareWith],
          },
        };
      }

      if (owner) {
        // eslint-disable-next-line custom-rules/require-try-catch-for-exhaustive
        const createdByWhere = match(owner as OwnerFilterOption)
          .with(OwnerFilterOption.ANYONE, () => {
            return {};
          })
          .with(OwnerFilterOption.IM_NOT_THE_OWNER, () => {
            return {
              createdBy: {
                id: {
                  _ne: currentUserId,
                },
              },
            };
          })
          .with(OwnerFilterOption.IM_THE_OWNER, () => {
            return {
              createdBy: {
                id: {
                  _eq: currentUserId,
                },
              },
            };
          })
          .with(OwnerFilterOption.SPECIFIC_PERSON, () => {
            if (!ownerList?.length) {
              return {};
            }

            return {
              createdBy: {
                id: {
                  _in: ownerList,
                },
              },
            };
          })
          .exhaustive();

        result = {
          ...result,
          ...createdByWhere,
        };
      }

      return result;
    },
    [currentUserId],
  );

  useEffect(() => {
    const formSubscription = filtersForm.watch(
      ({ department, language, owner, shareWith, ownerList, name }) => {
        const getArrayValues = (rawValues: (any | undefined)[]) =>
          Array.from(rawValues ?? []).filter((value) => value !== undefined);

        setWhere(
          getFilterWhereValue({
            department: getArrayValues(Array.from(department ?? [])),
            language: getArrayValues(Array.from(language ?? [])),
            owner,
            name,
            ownerList: getArrayValues(Array.from(ownerList ?? [])),
            shareWith: getArrayValues(Array.from(shareWith ?? [])),
          }),
        );
      },
    );

    return () => formSubscription.unsubscribe();
  }, [filtersForm, getFilterWhereValue]);

  useEffect(() => {
    if (!visibilityHandler.visible) {
      setSelectedTemplate(undefined);
    }
  }, [visibilityHandler.visible]);

  useEffect(() => {
    if (!upsertMessageTemplateModalVisibilityHandler.visible) {
      setTemplateToEdit(undefined);
    }
  }, [upsertMessageTemplateModalVisibilityHandler.visible]);

  const {
    handleLoadBottom,
    firstItemIndex,
    loadingMoreBottom,
    nodes,
    data,
    refetch,
    loading: loadingTemplates,
  } = useContactInteractionMessageTemplatesQuery({
    skip: !visibilityHandler.visible,
    where: filterWhere,
  });

  const templatesCount = useMemo(
    () => data?.contactInteractionMessageTemplates?.totalCount,
    [data?.contactInteractionMessageTemplates?.totalCount],
  );

  const showCreateMessageTemplateModal = useCallback(() => {
    setSelectedTemplate(undefined);
    upsertMessageTemplateModalVisibilityHandler.show();
  }, [upsertMessageTemplateModalVisibilityHandler]);

  const listContent = useMemo(() => {
    return match({ loadingTemplates, nodes })
      .with({ loadingTemplates: true, nodes: [] }, () => (
        <LoadingContainer>
          <LoadingIndicator />
        </LoadingContainer>
      ))
      .with({ nodes: [], loadingTemplates: false }, () => (
        <ListEmptyState onAddMessageTemplate={showCreateMessageTemplateModal} />
      ))
      .with({ nodes: P.when((nodes) => nodes.length > 0) }, ({ nodes }) => (
        <Virtuoso
          ref={virtuosoRef}
          data-lgg-id="contact-interaction-message-templates-list"
          data={nodes}
          initialItemCount={0}
          initialTopMostItemIndex={0}
          endReached={async () => {
            await handleLoadBottom();
          }}
          firstItemIndex={firstItemIndex}
          components={{
            Footer: () =>
              loadingMoreBottom ? (
                <ListLoadingItem visible={loadingMoreBottom} />
              ) : (
                <TemplateListFooter />
              ),
          }}
          itemContent={(index, template) => (
            <TemplateListItem
              template={template}
              key={template.id}
              selected={selectedTemplate?.id === template.id}
              onClick={() => {
                setSelectedTemplate(template);
              }}
              onDelete={refetch}
              onEdit={() => {
                setTemplateToEdit(template);
                upsertMessageTemplateModalVisibilityHandler.show();
              }}
              isFirstItem={firstItemIndex === index}
            />
          )}
        />
      ))
      .run();
  }, [
    firstItemIndex,
    handleLoadBottom,
    loadingMoreBottom,
    loadingTemplates,
    nodes,
    refetch,
    selectedTemplate?.id,
    showCreateMessageTemplateModal,
    upsertMessageTemplateModalVisibilityHandler,
  ]);

  const optionsConfigMap = useMemo(() => {
    return {
      ALL: {
        icon: 'buildingBlocks',
        label: t(
          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.visibilityOptions.allTemplates',
        ),
      },
      CREATED_BY_ME: {
        icon: 'contact',
        label: t(
          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.visibilityOptions.createdByMe',
        ),
      },
      EVERYONE: {
        icon: 'everyone',
        label: t(
          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.visibilityOptions.public',
        ),
      },
      ONLY_ME: {
        icon: 'lock',
        label: t(
          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.visibilityOptions.onlyMe',
        ),
      },
    };
  }, [t]);

  const visibilityFilterContent = useMemo(() => {
    const selectedOptionConfig = optionsConfigMap[visibilityFilter];
    const formResetConfig = {
      keepDefaultValues: true,
    };

    const options = [
      {
        ...optionsConfigMap.ALL,
        onClick: () => {
          setVisibilityFilter(MessageTemplateVisibility.ALL);

          filtersForm.reset();
        },
        'data-lgg-id': 'visibility-filter-option-all-templates',
        value: MessageTemplateVisibility.ALL,
      },
      {
        ...optionsConfigMap.CREATED_BY_ME,
        onClick: () => {
          setVisibilityFilter(MessageTemplateVisibility.CREATED_BY_ME);

          filtersForm.reset(
            {
              ...defaultFilterFormValues,
              owner: OwnerFilterOption.IM_THE_OWNER,
            },
            formResetConfig,
          );
        },
        'data-lgg-id': 'visibility-filter-option-created-by-me',
        value: MessageTemplateVisibility.CREATED_BY_ME,
      },
      {
        ...optionsConfigMap.EVERYONE,
        onClick: () => {
          setVisibilityFilter(MessageTemplateVisibility.EVERYONE);

          filtersForm.reset(
            {
              ...defaultFilterFormValues,
              shareWith: [MessageTemplateVisibility.EVERYONE],
            },
            formResetConfig,
          );
        },
        'data-lgg-id': 'visibility-filter-option-public',
        value: MessageTemplateVisibility.EVERYONE,
      },
      {
        ...optionsConfigMap.ONLY_ME,
        onClick: () => {
          setVisibilityFilter(MessageTemplateVisibility.ONLY_ME);

          filtersForm.reset(
            {
              ...defaultFilterFormValues,
              owner: OwnerFilterOption.IM_THE_OWNER,
              shareWith: [MessageTemplateVisibility.ONLY_ME],
            },
            formResetConfig,
          );
        },
        'data-lgg-id': 'visibility-filter-option-private',
        value: MessageTemplateVisibility.ONLY_ME,
      },
    ];

    if (breakpointUpMd) {
      return (
        <VisibilityFilterDropdown
          data-lgg-id="visibility-filter-trigger-button"
          size="small"
          icon={selectedOptionConfig.icon}
          variant="defaultWhite"
          options={options}
          selectedValue={visibilityFilter}
          visibilityHandler={visibilityFilterVisibilityHandler}
          customDropdownProps={{
            overlayStyle: { minWidth: '147px' },
          }}
        >
          {selectedOptionConfig.label}
        </VisibilityFilterDropdown>
      );
    } else {
      return (
        <>
          <MobileDropdownButton
            isActive={visibilityFilterVisibilityHandler.visible}
            size="small"
            variant="defaultGhost"
            onClick={visibilityFilterVisibilityHandler.show}
          >
            {selectedOptionConfig.label}
          </MobileDropdownButton>
          <MobileAddTemplateButton
            variant="defaultGhost"
            size="small"
            icon="plus"
            onClick={showCreateMessageTemplateModal}
          />
          <SelectableOptionsDrawer
            visible={visibilityFilterVisibilityHandler.visible}
            zIndex={ModalsZIndex}
            title={t('common:show')}
            onClose={visibilityFilterVisibilityHandler.close}
            selectedValue={visibilityFilter}
            options={options}
          />
        </>
      );
    }
  }, [
    breakpointUpMd,
    filtersForm,
    optionsConfigMap,
    showCreateMessageTemplateModal,
    t,
    visibilityFilter,
    visibilityFilterVisibilityHandler,
  ]);

  const handleSubmit = useCallback(() => {
    if (selectedTemplate) {
      const newEditorState = editor.parseEditorState(selectedTemplate.content);

      newEditorState.read(() => {
        if (!contact) {
          return;
        }

        const removeMutationListener = editor.registerMutationListener(
          TemplatePresetPlaceholderResolver,
          (nodeMap) => {
            nodeMap.entries().forEach(([nodeKey]) => {
              const node = $getNodeByKey(
                nodeKey,
              ) as TemplatePresetPlaceholderResolver | null;

              if (node) {
                editor.update(() => {
                  const updatedValue = getPresetPlaceholderValue(node.presetKey, contact);
                  node.getWritable().value = updatedValue ?? undefined;
                });
              }
            });
          },
          {
            skipInitialization: false,
          },
        );

        editor.setEditorState(newEditorState);

        removeMutationListener();
      });

      visibilityHandler.close();
    }
  }, [contact, editor, selectedTemplate, visibilityHandler]);

  const messageTemplatesFilters = useMemo(
    () => (
      <MessageTemplatesFilters
        mainForm={filtersForm}
        onReset={() => {
          setVisibilityFilter(MessageTemplateVisibility.ALL);
        }}
        onChange={() => {
          setVisibilityFilter(MessageTemplateVisibility.ALL);
        }}
      />
    ),
    [filtersForm],
  );

  const pluginTriggerIcon = useMemo(
    () => (
      <PluginIcon
        tooltip={t(
          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.tooltipText',
        )}
        type="messageTemplates"
        lggTestId="insert-contact-interaction-message-template-plugin-trigger"
        onClick={() => {
          if (disabled) {
            return;
          }

          visibilityHandler.show();
        }}
        isOpen={visibilityHandler.visible}
        disabled={disabled}
      />
    ),
    [disabled, t, visibilityHandler],
  );

  if (disabled) {
    return pluginTriggerIcon;
  }

  return (
    <div>
      {pluginTriggerIcon}
      <TemplatePickerModal<ContactInteractionMessageTemplate>
        testId="contact-interaction-message-templates-modal"
        pickerTitle={t(
          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.title',
        )}
        headerExtraContent={
          <ButtonV2
            variant="default"
            size="small"
            onClick={showCreateMessageTemplateModal}
            data-lgg-id="add-message-template-header-button"
          >
            {t(
              'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.addMessageTemplate',
            )}
          </ButtonV2>
        }
        selectedTemplate={selectedTemplate}
        itemsCount={templatesCount ?? 0}
        filtersContent={
          <FlexRow>
            {visibilityFilterContent}
            {breakpointUpMd ? messageTemplatesFilters : null}
          </FlexRow>
        }
        visibilityHandler={visibilityHandler}
        onSubmit={() => {
          editor.read(() => {
            const isInputEmpty = $getRoot().getTextContent().length === 0;

            if (isInputEmpty) {
              handleSubmit();
              return;
            }

            showConfirmationModal({
              confirmButtonText: t(
                'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.insertConfirmationModal.applyTemplate',
              ),
              message: t(
                'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.insertConfirmationModal.content',
              ),
              onConfirm: handleSubmit,
              title: t(
                'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.insertConfirmationModal.title',
              ),
              testId: 'replace-current-message-confirmation-modal',
              confirmButtonType: 'primary',
            });
          });
        }}
        listContent={listContent}
        headerBottomContent={messageTemplatesFilters}
        submitButtonLabel={t(
          'conversations:messageInput.options.messageTemplates.insertTemplatePlugin.modal.insertTemplate',
        )}
      />
      <UpsertMessageTemplateModal
        key={`${upsertMessageTemplateModalVisibilityHandler.visible}`}
        visibilityHandler={upsertMessageTemplateModalVisibilityHandler}
        template={templateToEdit}
        onCreate={refetch}
      />
    </div>
  );
};
